/**
 * Global Language Detection and Translation System
 * 
 * Provides automatic language detection and translation management
 * for the BCE system. Supports 8 languages with automatic fallback.
 * 
 * @class I18nManager
 */
class I18nManager {
    constructor() {
        this.currentLanguage = null;
        this.fallbackLanguage = 'en-gb';
        this.translations = {};
        this.loadedLanguages = new Set();
        this.loadingPromises = new Map();
        this.supportedLanguages = [
            'de-de', 'en-gb', 'en-us', 'es', 'fr-fr', 'pl-pl', 'int-en', 'zh-cn'
        ];
        
        this.init();
    }

    init() {
        this.detectLanguage();
        // Load current language and fallback language asynchronously
        this.loadLanguage(this.currentLanguage);
        if (this.currentLanguage !== this.fallbackLanguage) {
            this.loadLanguage(this.fallbackLanguage);
        }
    }

    detectLanguage() {
        // Method 1: Try to detect from URL pattern
        const urlLang = this.detectFromURL();
        if (urlLang) {
            this.currentLanguage = urlLang;
            return;
        }

        // Method 2: Try to detect from hreflang links
        const hreflangLang = this.detectFromHreflang();
        if (hreflangLang) {
            this.currentLanguage = hreflangLang;
            return;
        }

        // Method 3: Try to detect from HTML lang attribute
        const htmlLang = this.detectFromHTMLLang();
        if (htmlLang) {
            this.currentLanguage = htmlLang;
            return;
        }

        // Method 4: Try to detect from browser language
        const browserLang = this.detectFromBrowser();
        if (browserLang) {
            this.currentLanguage = browserLang;
            return;
        }

        // Fallback
        this.currentLanguage = this.fallbackLanguage;
    }

    detectFromURL() {
        const url = window.location.href;
        const hostname = window.location.hostname;
        
        // Check for subdomain patterns like cn.domain.com
        if (hostname.includes('.cn')) {
            return 'zh-cn';
        }
        
        // Check for URL patterns like /de-de/, /uk-en/, etc.
        const urlPatterns = [
            { pattern: /\/de-de\//i, lang: 'de-de' },
            { pattern: /\/uk-en\//i, lang: 'en-gb' },
            { pattern: /\/us-en\//i, lang: 'en-us' },
            { pattern: /\/int-es\//i, lang: 'es' },
            { pattern: /\/fr-fr\//i, lang: 'fr-fr' },
            { pattern: /\/pl-pl\//i, lang: 'pl-pl' },
            { pattern: /\/int-en\//i, lang: 'int-en' },
            { pattern: /binder-world\.cn/i, lang: 'zh-cn' }
        ];

        for (const { pattern, lang } of urlPatterns) {
            if (pattern.test(url)) {
                return lang;
            }
        }

        return null;
    }

    detectFromHreflang() {
        // Look for hreflang links or the hreflangLinks div
        const hreflangDiv = document.getElementById('hreflangLinks');
        if (hreflangDiv) {
            const currentURL = window.location.href;
            const links = hreflangDiv.querySelectorAll('a');
            
            for (const link of links) {
                const href = link.getAttribute('href');
                if (href && currentURL.includes(href.split('/').slice(-1)[0])) {
                    const langCode = link.textContent.trim().toLowerCase();
                    return this.normalizeLangCode(langCode);
                }
            }
        }

        // Also check for standard hreflang links
        const hreflangLinks = document.querySelectorAll('link[hreflang]');
        const currentURL = window.location.href;
        
        for (const link of hreflangLinks) {
            const href = link.getAttribute('href');
            const hreflang = link.getAttribute('hreflang');
            
            if (href && currentURL.includes(href)) {
                return this.normalizeLangCode(hreflang);
            }
        }

        return null;
    }

    detectFromHTMLLang() {
        const htmlLang = document.documentElement.lang;
        if (htmlLang) {
            return this.normalizeLangCode(htmlLang);
        }
        return null;
    }

    detectFromBrowser() {
        const browserLang = navigator.language || navigator.userLanguage;
        if (browserLang) {
            return this.normalizeLangCode(browserLang);
        }
        return null;
    }

    normalizeLangCode(langCode) {
        if (!langCode) return null;
        
        const normalized = langCode.toLowerCase().replace('_', '-');
        
        // Map common variations to our supported languages
        const langMap = {
            'de': 'de-de',
            'de-de': 'de-de',
            'en': 'en-gb',
            'en-gb': 'en-gb',
            'en-uk': 'en-gb',
            'uk-en': 'en-gb',
            'en-us': 'en-us',
            'us-en': 'en-us',
            'es': 'es',
            'int-es': 'es',
            'fr': 'fr-fr',
            'fr-fr': 'fr-fr',
            'pl': 'pl-pl',
            'pl-pl': 'pl-pl',
            'int-en': 'int-en',
            'zh': 'zh-cn',
            'zh-cn': 'zh-cn',
            'cn': 'zh-cn'
        };

        return langMap[normalized] || null;
    }

    async loadLanguage(langCode) {
        if (!langCode || this.loadedLanguages.has(langCode)) {
            return;
        }

        // If already loading, return the existing promise
        if (this.loadingPromises.has(langCode)) {
            return this.loadingPromises.get(langCode);
        }

        const loadPromise = this.fetchTranslations(langCode);
        this.loadingPromises.set(langCode, loadPromise);

        try {
            const translations = await loadPromise;
            this.translations[langCode] = translations;
            this.loadedLanguages.add(langCode);
            // Translation loaded successfully
        } catch (error) {
            console.error(`[i18n] Failed to load translations for ${langCode}:`, error);
        } finally {
            this.loadingPromises.delete(langCode);
        }
    }

    async fetchTranslations(langCode) {
        // Use inline translations instead of dynamic imports for TYPO3 compatibility
        return this.getInlineTranslations(langCode);
    }

    getInlineTranslations(langCode) {
        const allTranslations = {
            'de-de': {
                "event": {
                    "daysToGo_other": "Nur noch {count} Tage!",
                    "daysToGo_one": "Nur noch 1 Tag!",
                    "startingSoon": "Beginnt bald!",
                    "visitNow": "Besuchen Sie uns jetzt:",
                    "at": "am"
                },
                "slider": {
                    "next": "Weiter",
                    "previous": "Zurück",
                    "loading": "Lädt...",
                    "slideOf": "Bild {current} von {total}"
                },
                "toc": {
                    "headline": "Inhaltsübersicht"
                },
                "contentLimiter": {
                    "showMore": "Mehr anzeigen",
                    "showLess": "Weniger anzeigen"
                },
                "common": {
                    "loading": "Lädt...",
                    "error": "Fehler",
                    "retry": "Erneut versuchen",
                    "close": "Schließen"
                }
            },
            'en-gb': {
                "event": {
                    "daysToGo_other": "Only {count} days to go!",
                    "daysToGo_one": "Only 1 day to go!",
                    "startingSoon": "Starting soon!",
                    "visitNow": "Visit us now at:",
                    "at": "at"
                },
                "slider": {
                    "next": "Next",
                    "previous": "Previous",
                    "loading": "Loading...",
                    "slideOf": "Slide {current} of {total}"
                },
                "toc": {
                    "headline": "Table of Contents"
                },
                "contentLimiter": {
                    "showMore": "Show more",
                    "showLess": "Show less"
                },
                "common": {
                    "loading": "Loading...",
                    "error": "Error",
                    "retry": "Try again",
                    "close": "Close"
                }
            },
            'en-us': {
                "event": {
                    "daysToGo_other": "Only {count} days to go!",
                    "daysToGo_one": "Only 1 day to go!",
                    "startingSoon": "Starting soon!",
                    "visitNow": "Visit us now at:",
                    "at": "at"
                },
                "slider": {
                    "next": "Next",
                    "previous": "Previous",
                    "loading": "Loading...",
                    "slideOf": "Slide {current} of {total}"
                },
                "toc": {
                    "headline": "Table of Contents"
                },
                "contentLimiter": {
                    "showMore": "Show more",
                    "showLess": "Show less"
                },
                "common": {
                    "loading": "Loading...",
                    "error": "Error",
                    "retry": "Try again",
                    "close": "Close"
                }
            },
            'es': {
                "event": {
                    "daysToGo_other": "¡Solo quedan {count} días!",
                    "daysToGo_one": "¡Solo queda 1 día!",
                    "startingSoon": "¡Comienza pronto!",
                    "visitNow": "Visítanos ahora en:",
                    "at": "en"
                },
                "slider": {
                    "next": "Siguiente",
                    "previous": "Anterior",
                    "loading": "Cargando...",
                    "slideOf": "Imagen {current} de {total}"
                },
                "toc": {
                    "headline": "Índice de contenidos"
                },
                "contentLimiter": {
                    "showMore": "Mostrar más",
                    "showLess": "Mostrar menos"
                },
                "common": {
                    "loading": "Cargando...",
                    "error": "Error",
                    "retry": "Intentar de nuevo",
                    "close": "Cerrar"
                }
            },
            'fr-fr': {
                "event": {
                    "daysToGo_other": "Plus que {count} jours !",
                    "daysToGo_one": "Plus qu'1 jour !",
                    "startingSoon": "Commence bientôt !",
                    "visitNow": "Visitez-nous maintenant à :",
                    "at": "à"
                },
                "slider": {
                    "next": "Suivant",
                    "previous": "Précédent",
                    "loading": "Chargement...",
                    "slideOf": "Image {current} sur {total}"
                },
                "toc": {
                    "headline": "Table des matières"
                },
                "contentLimiter": {
                    "showMore": "Afficher plus",
                    "showLess": "Afficher moins"
                },
                "common": {
                    "loading": "Chargement...",
                    "error": "Erreur",
                    "retry": "Réessayer",
                    "close": "Fermer"
                }
            },
            'pl-pl': {
                "event": {
                    "daysToGo_other": "Tylko {count} dni do rozpoczęcia!",
                    "daysToGo_one": "Tylko 1 dzień do rozpoczęcia!",
                    "startingSoon": "Zaczyna się wkrótce!",
                    "visitNow": "Odwiedź nas teraz w:",
                    "at": "w"
                },
                "slider": {
                    "next": "Dalej",
                    "previous": "Wstecz",
                    "loading": "Ładowanie...",
                    "slideOf": "Slajd {current} z {total}"
                },
                "toc": {
                    "headline": "Spis treści"
                },
                "contentLimiter": {
                    "showMore": "Pokaż więcej",
                    "showLess": "Pokaż mniej"
                },
                "common": {
                    "loading": "Ładowanie...",
                    "error": "Błąd",
                    "retry": "Spróbuj ponownie",
                    "close": "Zamknij"
                }
            },
            'int-en': {
                "event": {
                    "daysToGo_other": "Only {count} days to go!",
                    "daysToGo_one": "Only 1 day to go!",
                    "startingSoon": "Starting soon!",
                    "visitNow": "Visit us now at:",
                    "at": "at"
                },
                "slider": {
                    "next": "Next",
                    "previous": "Previous",
                    "loading": "Loading...",
                    "slideOf": "Slide {current} of {total}"
                },
                "toc": {
                    "headline": "Table of Contents"
                },
                "contentLimiter": {
                    "showMore": "Show more",
                    "showLess": "Show less"
                },
                "common": {
                    "loading": "Loading...",
                    "error": "Error",
                    "retry": "Try again",
                    "close": "Close"
                }
            },
            'zh-cn': {
                "event": {
                    "daysToGo_other": "还有{count}天！",
                    "daysToGo_one": "还有1天！",
                    "startingSoon": "即将开始！",
                    "visitNow": "现在访问我们：",
                    "at": "在"
                },
                "slider": {
                    "next": "下一个",
                    "previous": "上一个",
                    "loading": "加载中...",
                    "slideOf": "第{current}张，共{total}张"
                },
                "toc": {
                    "headline": "目录"
                },
                "contentLimiter": {
                    "showMore": "显示更多",
                    "showLess": "显示更少"
                },
                "common": {
                    "loading": "加载中...",
                    "error": "错误",
                    "retry": "重试",
                    "close": "关闭"
                }
            }
        };

        return allTranslations[langCode] || allTranslations['en-gb'];
    }

    // Public API methods
    getLanguage() {
        return this.currentLanguage;
    }

    getLanguageShort() {
        // Return just the language part (de, en, es, etc.)
        if (!this.currentLanguage) return 'en';
        return this.currentLanguage.split('-')[0];
    }

    isGerman() {
        return this.getLanguageShort() === 'de';
    }

    isEnglish() {
        const lang = this.getLanguageShort();
        return lang === 'en';
    }

    isChinese() {
        return this.currentLanguage === 'zh-cn';
    }

    /**
     * Translate a key with optional interpolation and pluralization
     * 
     * @param {string} module - Module name (e.g., 'event', 'slider')
     * @param {string} key - Translation key (e.g., 'daysToGo', 'next')
     * @param {...any} args - Arguments for interpolation (first arg can be number for pluralization)
     * @returns {string} Translated string or fallback key in brackets
     * 
     * @example
     * i18n.t('event', 'daysToGo', 5) // "Nur noch 5 Tage!" (German)
     * i18n.t('slider', 'next') // "Weiter" (German) or "Next" (English)
     */
    t(module, key, ...args) {
        const lang = this.currentLanguage || this.fallbackLanguage;
        
        // Handle pluralization for numbers FIRST
        if (args.length > 0 && typeof args[0] === 'number') {
            const count = args[0];
            const pluralKey = count === 1 ? `${key}_one` : `${key}_other`;
            
            // Try current language
            let translation = this.getTranslation(lang, module, pluralKey);
            
            // Try fallback
            if (!translation) {
                translation = this.getTranslation(this.fallbackLanguage, module, pluralKey);
            }
            
            if (translation) {
                return this.interpolate(translation, args);
            }
        }
        
        // Try normal key
        let translation = this.getTranslation(lang, module, key);
        
        if (!translation) {
            // Try fallback language
            translation = this.getTranslation(this.fallbackLanguage, module, key);
            if (!translation) {
                // Never surface a raw [MODULE.KEY] placeholder to the user — output
                // nothing. The warning is stripped from the production build.
                console.warn(`[i18n] Missing translation: ${module}.${key}`);
                return '';
            }
        }
        
        // Interpolate variables
        if (args.length > 0) {
            return this.interpolate(translation, args);
        }
        
        return translation;
    }

    getTranslation(lang, module, key) {
        const loaded = this.translations[lang]?.[module]?.[key];
        if (loaded !== undefined) {
            return loaded;
        }
        // Translations are inline (synchronous). If the async cache hasn't been
        // populated yet, read them directly so t() never returns the [MODULE.KEY]
        // fallback just because of load timing.
        return this.getInlineTranslations(lang)?.[module]?.[key];
    }

    interpolate(template, args) {
        if (typeof template !== 'string') {
            return template;
        }
        
        // Support both {count} and positional {0}, {1} syntax
        return template.replace(/\{(\w+|\d+)\}/g, (match, key) => {
            if (key === 'count') {
                return args[0] || match;
            }
            if (/^\d+$/.test(key)) {
                return args[parseInt(key)] || match;
            }
            // For named parameters, look for them in args object if passed
            if (args.length === 1 && typeof args[0] === 'object') {
                return args[0][key] || match;
            }
            return match;
        });
    }

    // Add or update translations
    addTranslations(lang, module, translations) {
        if (!this.translations[lang]) {
            this.translations[lang] = {};
        }
        if (!this.translations[lang][module]) {
            this.translations[lang][module] = {};
        }
        
        Object.assign(this.translations[lang][module], translations);
    }

    // Debug method
    debug() {
        return {
            detectedLanguage: this.currentLanguage,
            url: window.location.href,
            htmlLang: document.documentElement.lang,
            browserLang: navigator.language,
            supportedLanguages: this.supportedLanguages
        };
    }
}

// Create global instance
const i18n = new I18nManager();

// Export for use in modules
export default i18n;